import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  Mail, Send, Download, FileText, Search, Plus, Calendar, Clock,
  X, Loader2, CheckCircle, AlertTriangle, Shield, Eye,
} from 'lucide-react';

const STATUS_PILL = {
  sent: { label: 'Sent', cls: 'bg-emerald-100 text-emerald-700' },
  partial_failure: { label: 'Partial', cls: 'bg-amber-100 text-amber-700' },
  scheduled: { label: 'Scheduled', cls: 'bg-blue-100 text-blue-700' },
  sending: { label: 'Sending…', cls: 'bg-blue-100 text-blue-700' },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-700' },
  draft: { label: 'Draft', cls: 'bg-gray-100 text-gray-700' },
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

/**
 * Admin Correspondence Tab — list + compose + view.
 * All records are immutable per compliance policy: no edit/delete UI.
 */
const AdminCorrespondenceTab = ({ apiUrl, adminPassword }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [composeOpen, setComposeOpen] = useState(false);
  const [viewing, setViewing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${apiUrl}/api/correspondence`, {
        headers: { 'X-Admin-Password': adminPassword || '' },
      });
      if (r.ok) setList(await r.json());
    } finally { setLoading(false); }
  }, [apiUrl, adminPassword]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return list;
    return list.filter(c =>
      (c.doc_number || '').toLowerCase().includes(q) ||
      (c.subject || '').toLowerCase().includes(q) ||
      (c.created_by_admin_name || '').toLowerCase().includes(q)
    );
  }, [list, search]);

  const handleDownload = async (id, filename) => {
    const r = await fetch(`${apiUrl}/api/correspondence/${id}/pdf`, {
      headers: { 'X-Admin-Password': adminPassword || '' },
    });
    if (!r.ok) { alert('Download failed'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'correspondence.pdf';
    a.click();
    URL.revokeObjectURL(url);
  };

  const openView = async (id) => {
    const r = await fetch(`${apiUrl}/api/correspondence/${id}`, {
      headers: { 'X-Admin-Password': adminPassword || '' },
    });
    if (r.ok) setViewing(await r.json());
  };

  return (
    <Card data-testid="admin-correspondence-tab">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-[#0077B6]" /> Correspondence
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
              <Shield className="h-3 w-3" /> Immutable
            </span>
          </CardTitle>
          <p className="text-xs text-gray-500 mt-1">
            Official memos &amp; business communications sent across divisions.
            Every record is stamped with a document number, date/time, and is
            retained permanently for compliance and audit purposes — no edit or
            delete is possible.
          </p>
        </div>
        <Button onClick={() => setComposeOpen(true)} className="bg-[#0077B6]" data-testid="new-correspondence-btn">
          <Plus className="h-4 w-4 mr-1" /> New Correspondence
        </Button>
      </CardHeader>
      <CardContent>
        <div className="mb-3 relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by doc#, subject, or sender…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-sm border rounded-md focus:ring-2 focus:ring-[#0077B6]"
            data-testid="correspondence-search"
          />
        </div>

        {loading ? (
          <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            <Mail className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No correspondence records yet.</p>
            <p className="text-xs text-gray-400 mt-1">Click <b>New Correspondence</b> to send your first memo.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-600 uppercase border-b">
                <tr>
                  <th className="text-left py-2 px-3">Doc #</th>
                  <th className="text-left py-2 px-3">Subject</th>
                  <th className="text-left py-2 px-3">Recipients</th>
                  <th className="text-left py-2 px-3">Replies</th>
                  <th className="text-left py-2 px-3">Status</th>
                  <th className="text-left py-2 px-3">Sent / Scheduled</th>
                  <th className="text-left py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => {
                  const pill = STATUS_PILL[c.status] || STATUS_PILL.draft;
                  const rcount = (c.recipients?.length || 0) + (c.cc_emails?.length || 0) + (c.bcc_emails?.length || 0);
                  const when = c.sent_at || c.scheduled_at || c.created_at;
                  const adminUnread = (c.unread_for || []).includes('admin');
                  return (
                    <tr key={c.id} className={`border-b hover:bg-gray-50 ${adminUnread ? 'bg-blue-50/40' : ''}`} data-testid={`corr-row-${c.id}`}>
                      <td className="py-2 px-3 font-mono text-xs whitespace-nowrap">{c.doc_number}</td>
                      <td className="py-2 px-3 max-w-md truncate" title={c.subject}>{c.subject}</td>
                      <td className="py-2 px-3 text-xs text-gray-600">{rcount}</td>
                      <td className="py-2 px-3 text-xs">
                        <span className="inline-flex items-center gap-1">
                          {(c.replies_count || 0) > 0 ? (
                            <span className="font-semibold text-[#0077B6]">{c.replies_count}</span>
                          ) : <span className="text-gray-400">0</span>}
                          {adminUnread && (
                            <span className="bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full" title="New reply">NEW</span>
                          )}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${pill.cls}`}>
                          {pill.label}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(when)}</td>
                      <td className="py-2 px-3 whitespace-nowrap">
                        <button
                          onClick={() => openView(c.id)}
                          className="text-[#0077B6] hover:text-[#003D5C] p-1"
                          title="View thread"
                          data-testid={`corr-view-${c.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDownload(c.id, c.pdf_filename)}
                          className="text-[#0077B6] hover:text-[#003D5C] p-1"
                          title="Download PDF"
                          data-testid={`corr-download-${c.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {composeOpen && (
        <ComposeCorrespondenceModal
          apiUrl={apiUrl}
          adminPassword={adminPassword}
          onClose={() => setComposeOpen(false)}
          onSent={() => { setComposeOpen(false); fetchList(); }}
        />
      )}

      {viewing && (
        <ViewCorrespondenceModal
          record={viewing}
          apiUrl={apiUrl}
          adminPassword={adminPassword}
          onChanged={fetchList}
          onClose={() => { setViewing(null); fetchList(); }}
          onDownload={() => handleDownload(viewing.id, viewing.pdf_filename)}
        />
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
function ComposeCorrespondenceModal({ apiUrl, adminPassword, onClose, onSent }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [source, setSource] = useState('generated');
  const [uploadedPdf, setUploadedPdf] = useState(null);
  const [allAgents, setAllAgents] = useState(false);
  const [divisions, setDivisions] = useState({ mortgage: false, insurance: false, investments: false });
  const [agentIds, setAgentIds] = useState([]);
  const [agents, setAgents] = useState([]);
  const [externalEmails, setExternalEmails] = useState('');
  const [ccEmails, setCcEmails] = useState('');
  const [bccEmails, setBccEmails] = useState('');
  const [schedule, setSchedule] = useState('');
  const [senderName, setSenderName] = useState('Utmost Financial Inc.');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    fetch(`${apiUrl}/api/agents`, { headers: { 'X-Admin-Password': adminPassword || '' } })
      .then(r => r.ok ? r.json() : [])
      .then(setAgents)
      .catch(() => setAgents([]));
  }, [apiUrl, adminPassword]);

  const toggleDivision = (k) => setDivisions(p => ({ ...p, [k]: !p[k] }));
  const toggleAgent = (id) => setAgentIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const submit = async () => {
    setErr('');
    if (!subject.trim()) { setErr('Subject is required.'); return; }
    if (source === 'generated' && !body.trim()) { setErr('Body text is required when generating the PDF.'); return; }
    if (source === 'uploaded' && !uploadedPdf) { setErr('Please attach a PDF file.'); return; }
    const noRecipients = !allAgents
      && !Object.values(divisions).some(Boolean)
      && agentIds.length === 0
      && !externalEmails.trim()
      && !ccEmails.trim()
      && !bccEmails.trim();
    if (noRecipients) { setErr('Please select at least one recipient (To / CC / BCC).'); return; }

    const fd = new FormData();
    fd.append('subject', subject);
    fd.append('source', source);
    if (source === 'generated') fd.append('body_text', body);
    if (source === 'uploaded' && uploadedPdf) fd.append('pdf_file', uploadedPdf);
    fd.append('all_agents', allAgents ? 'true' : 'false');
    fd.append('divisions_csv', Object.keys(divisions).filter(k => divisions[k]).join(','));
    fd.append('agent_ids_csv', agentIds.join(','));
    fd.append('external_emails_csv', externalEmails);
    fd.append('cc_csv', ccEmails);
    fd.append('bcc_csv', bccEmails);
    if (schedule) fd.append('scheduled_at', new Date(schedule).toISOString());
    fd.append('sender_name', senderName || 'Utmost Financial Inc.');

    setSubmitting(true);
    try {
      const r = await fetch(`${apiUrl}/api/correspondence`, {
        method: 'POST',
        headers: { 'X-Admin-Password': adminPassword || '' },
        body: fd,
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `Failed (${r.status})`);
      }
      onSent();
    } catch (e) {
      setErr(e.message || 'Send failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="compose-correspondence-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Send className="h-5 w-5 text-[#0077B6]" />
            Compose Correspondence
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {err && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-900 p-3 rounded text-sm flex items-start gap-2" data-testid="compose-error">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" /> {err}
            </div>
          )}

          {/* Subject */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Subject <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0077B6]"
              placeholder="e.g. Q2 2026 Compliance Reminder"
              data-testid="compose-subject"
            />
          </div>

          {/* Source toggle */}
          <div className="inline-flex p-1 bg-gray-100 rounded-lg">
            <button
              type="button"
              onClick={() => setSource('generated')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${source === 'generated' ? 'bg-white shadow text-[#003D5C]' : 'text-gray-600'}`}
              data-testid="compose-source-generated"
            >
              Generate PDF from text
            </button>
            <button
              type="button"
              onClick={() => setSource('uploaded')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${source === 'uploaded' ? 'bg-white shadow text-[#003D5C]' : 'text-gray-600'}`}
              data-testid="compose-source-uploaded"
            >
              Upload PDF
            </button>
          </div>

          {source === 'generated' ? (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Body <span className="text-red-500">*</span></label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0077B6] font-serif"
                placeholder={"Dear Team,\n\nThis is a polite notice that…\n\nBest regards,\nUtmost Financial Inc."}
                data-testid="compose-body"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                The PDF is auto-generated with Utmost letterhead, document number,
                date/time stamp, and an immutability footer.
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">PDF file <span className="text-red-500">*</span></label>
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setUploadedPdf(e.target.files?.[0] || null)}
                className="w-full text-sm"
                data-testid="compose-pdf-file"
              />
              {uploadedPdf && (
                <p className="text-[11px] text-gray-500 mt-1">
                  Selected: <b>{uploadedPdf.name}</b> ({(uploadedPdf.size / 1024).toFixed(0)} KB)
                </p>
              )}
            </div>
          )}

          <div className="border-t pt-3">
            <p className="text-xs font-semibold text-gray-700 mb-2">Recipients</p>

            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={allAgents}
                onChange={(e) => setAllAgents(e.target.checked)}
                data-testid="compose-all-agents"
              />
              <span className="text-sm">Send to <b>all active agents</b> (broadcast)</span>
            </label>

            <div className="grid grid-cols-3 gap-2 mb-3">
              {['mortgage', 'insurance', 'investments'].map(d => (
                <label key={d} className="flex items-center gap-2 text-sm border rounded px-2 py-1.5">
                  <input
                    type="checkbox"
                    checked={!!divisions[d]}
                    onChange={() => toggleDivision(d)}
                    data-testid={`compose-division-${d}`}
                  />
                  <span className="capitalize">{d} agents</span>
                </label>
              ))}
            </div>

            <details className="mb-2">
              <summary className="text-xs font-medium text-[#0077B6] cursor-pointer">
                + Add specific agents ({agentIds.length} selected)
              </summary>
              <div className="mt-2 max-h-44 overflow-y-auto border rounded p-2 text-sm" data-testid="compose-agent-list">
                {agents.length === 0 ? (
                  <p className="text-xs text-gray-500">No agents available.</p>
                ) : agents.map(a => (
                  <label key={a.id} className="flex items-center gap-2 py-0.5 hover:bg-gray-50 rounded px-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agentIds.includes(a.id)}
                      onChange={() => toggleAgent(a.id)}
                      data-testid={`compose-agent-${a.id}`}
                    />
                    <span className="text-xs"><b>{a.name}</b> <span className="text-gray-500">— {a.email}</span></span>
                  </label>
                ))}
              </div>
            </details>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">External recipients (comma-separated emails)</label>
              <input
                type="text"
                value={externalEmails}
                onChange={(e) => setExternalEmails(e.target.value)}
                placeholder="partner@example.com, broker@otherco.com"
                className="w-full border rounded px-3 py-2 text-xs focus:ring-2 focus:ring-[#0077B6]"
                data-testid="compose-external-emails"
              />
            </div>

            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">CC (comma-separated)</label>
                <input
                  type="text"
                  value={ccEmails}
                  onChange={(e) => setCcEmails(e.target.value)}
                  placeholder="cc1@x.com, cc2@x.com"
                  className="w-full border rounded px-3 py-2 text-xs focus:ring-2 focus:ring-[#0077B6]"
                  data-testid="compose-cc"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">BCC (comma-separated)</label>
                <input
                  type="text"
                  value={bccEmails}
                  onChange={(e) => setBccEmails(e.target.value)}
                  placeholder="bcc1@x.com"
                  className="w-full border rounded px-3 py-2 text-xs focus:ring-2 focus:ring-[#0077B6]"
                  data-testid="compose-bcc"
                />
              </div>
            </div>
          </div>

          <div className="border-t pt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                <Clock className="inline h-3 w-3 mr-1" />
                Schedule send (optional)
              </label>
              <input
                type="datetime-local"
                value={schedule}
                onChange={(e) => setSchedule(e.target.value)}
                className="w-full border rounded px-3 py-2 text-xs focus:ring-2 focus:ring-[#0077B6]"
                data-testid="compose-schedule"
              />
              <p className="text-[10px] text-gray-500 mt-1">Leave blank to send immediately.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Sender name (on PDF letterhead)</label>
              <input
                type="text"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                className="w-full border rounded px-3 py-2 text-xs focus:ring-2 focus:ring-[#0077B6]"
                data-testid="compose-sender"
              />
            </div>
          </div>
        </div>
        <div className="p-4 border-t flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#0077B6]" data-testid="compose-send-btn">
            {submitting ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Working…</>) : (
              <><Send className="h-4 w-4 mr-1" /> {schedule ? 'Schedule' : 'Send Now'}</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ViewCorrespondenceModal({ record, apiUrl, adminPassword, onClose, onDownload, onChanged }) {
  // Iter 47 — render the full thread + a reply composer. The "record" prop
  // is the lightweight list-row payload; we always re-fetch the full thread
  // (including replies) so View == Reply context.
  const [thread, setThread] = useState(record);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [replyFile, setReplyFile] = useState(null);
  const [replying, setReplying] = useState(false);
  const [replyErr, setReplyErr] = useState('');
  const pill = STATUS_PILL[record.status] || STATUS_PILL.draft;

  const fetchThread = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${apiUrl}/api/correspondence/${record.id}/thread`, {
        headers: { 'X-Admin-Password': adminPassword || '' },
      });
      if (r.ok) {
        const d = await r.json();
        setThread(d);
        // Clear the admin's unread badge on open
        if ((d.unread_for || []).includes('admin')) {
          fetch(`${apiUrl}/api/correspondence/${record.id}/mark-read`, {
            method: 'POST', headers: { 'X-Admin-Password': adminPassword || '' },
          }).then(() => onChanged && onChanged());
        }
      }
    } finally { setLoading(false); }
  }, [apiUrl, adminPassword, record.id, onChanged]);

  useEffect(() => { fetchThread(); }, [fetchThread]);

  const submitReply = async () => {
    setReplyErr('');
    if (!replyText.trim()) { setReplyErr('Reply body is required.'); return; }
    setReplying(true);
    try {
      const fd = new FormData();
      fd.append('body_text', replyText.trim());
      fd.append('author_type', 'admin');
      fd.append('author_name', record.created_by_admin_name || 'Utmost Admin');
      if (replyFile) fd.append('attachment', replyFile);
      const r = await fetch(`${apiUrl}/api/correspondence/${record.id}/reply`, {
        method: 'POST',
        headers: { 'X-Admin-Password': adminPassword || '' },
        body: fd,
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Reply failed');
      }
      setReplyText('');
      setReplyFile(null);
      await fetchThread();
      onChanged && onChanged();
    } catch (e) {
      setReplyErr(e.message || 'Reply failed');
    } finally { setReplying(false); }
  };

  const downloadReplyAttachment = async (replyId, filename) => {
    const r = await fetch(`${apiUrl}/api/correspondence/${record.id}/reply-attachment/${replyId}`, {
      headers: { 'X-Admin-Password': adminPassword || '' },
    });
    if (!r.ok) { alert('Download failed'); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename || 'attachment'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="view-correspondence-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-white">
          <div>
            <p className="font-mono text-xs text-gray-500">{record.doc_number}</p>
            <h2 className="text-lg font-semibold">{record.subject}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div className="flex flex-wrap gap-3 items-center text-xs">
            <span className={`font-semibold px-2 py-0.5 rounded ${pill.cls}`}>{pill.label}</span>
            <span className="text-gray-500"><Calendar className="inline h-3 w-3 mr-1" />Created: {fmtDateTime(record.created_at)}</span>
            {record.scheduled_at && <span className="text-gray-500"><Clock className="inline h-3 w-3 mr-1" />Scheduled: {fmtDateTime(record.scheduled_at)}</span>}
            {record.sent_at && <span className="text-gray-500"><CheckCircle className="inline h-3 w-3 mr-1 text-emerald-600" />Sent: {fmtDateTime(record.sent_at)}</span>}
            <span className="text-gray-500"><Shield className="inline h-3 w-3 mr-1 text-amber-600" /> Immutable</span>
          </div>

          <div className="bg-gray-50 border rounded p-3">
            <p className="text-xs font-semibold text-gray-700 mb-2">Recipients ({record.recipients?.length || 0})</p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {(record.recipients || []).map((r, i) => (
                <div key={i} className="text-xs flex justify-between gap-2">
                  <span><b>{r.name || r.email}</b> <span className="text-gray-500">— {r.email}</span></span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${r.kind === 'agent' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                    {r.kind}
                  </span>
                </div>
              ))}
              {(record.cc_emails || []).map((e, i) => (
                <div key={`cc-${i}`} className="text-xs text-gray-600">CC: {e}</div>
              ))}
              {(record.bcc_emails || []).map((e, i) => (
                <div key={`bcc-${i}`} className="text-xs text-gray-600">BCC: {e}</div>
              ))}
            </div>
          </div>

          {thread.body_text && (
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1">Memo Body</p>
              <pre className="whitespace-pre-wrap font-serif text-sm bg-white border rounded p-3 max-h-40 overflow-y-auto">{thread.body_text}</pre>
            </div>
          )}

          {/* Replies thread */}
          <div className="border-t pt-3">
            <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
              <Mail className="h-3.5 w-3.5" /> Thread ({(thread.replies || []).length} {(thread.replies || []).length === 1 ? 'reply' : 'replies'})
            </p>
            {loading ? (
              <div className="py-4 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-gray-400" /></div>
            ) : (thread.replies || []).length === 0 ? (
              <p className="text-xs text-gray-500 italic">No replies yet.</p>
            ) : (
              <div className="space-y-2" data-testid="admin-thread-replies">
                {(thread.replies || []).map(rep => (
                  <ReplyBubble
                    key={rep.id}
                    reply={rep}
                    onDownloadAttachment={() => downloadReplyAttachment(rep.id, rep.attachment?.filename)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Admin reply composer */}
          <div className="border-t pt-3" data-testid="admin-reply-composer">
            <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
              <Send className="h-3.5 w-3.5" /> Reply (as Admin)
            </p>
            {replyErr && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-900 p-2 rounded text-xs mb-2">{replyErr}</div>
            )}
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={3}
              placeholder="Type your reply…"
              className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0077B6]"
              data-testid="admin-reply-text"
            />
            <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
              <input
                type="file"
                onChange={(e) => setReplyFile(e.target.files?.[0] || null)}
                className="text-xs"
                data-testid="admin-reply-attachment"
              />
              <Button onClick={submitReply} disabled={replying} className="bg-[#0077B6]" size="sm" data-testid="admin-reply-send-btn">
                {replying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                Post Reply
              </Button>
            </div>
          </div>
        </div>
        <div className="p-4 border-t flex justify-between items-center gap-2 sticky bottom-0 bg-white">
          <p className="text-[11px] text-gray-500 inline-flex items-center gap-1">
            <FileText className="h-3 w-3" /> {record.pdf_filename} · {((record.pdf_size_bytes || 0) / 1024).toFixed(0)} KB
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button onClick={onDownload} className="bg-[#0077B6]" data-testid="view-download-btn">
              <Download className="h-4 w-4 mr-1" /> Download PDF
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ReplyBubble({ reply, onDownloadAttachment }) {
  const isAdmin = reply.author_type === 'admin';
  return (
    <div
      className={`p-3 rounded-lg border ${isAdmin ? 'bg-amber-50/40 border-amber-200' : 'bg-blue-50/40 border-blue-200'}`}
      data-testid={`reply-bubble-${reply.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-xs font-semibold text-gray-800">
          {reply.author_name} <span className="text-[10px] uppercase ml-1 px-1.5 py-0.5 rounded bg-white border">{reply.author_type}</span>
        </span>
        <span className="text-[11px] text-gray-500">{fmtDateTime(reply.sent_at)}</span>
      </div>
      <pre className="whitespace-pre-wrap font-sans text-sm text-gray-900">{reply.body_text}</pre>
      {reply.attachment && (
        <button
          onClick={onDownloadAttachment}
          className="text-xs text-[#0077B6] hover:underline mt-2 inline-flex items-center gap-1"
          data-testid={`reply-attachment-${reply.id}`}
        >
          <Download className="h-3 w-3" /> {reply.attachment.filename} ({((reply.attachment.size_bytes || 0) / 1024).toFixed(0)} KB)
        </button>
      )}
      {/* Delivery hint */}
      {(reply.delivery_log || []).length > 0 && (
        <p className="text-[10px] text-gray-400 mt-1.5">
          Notified: {reply.delivery_log.filter(d => d.sent_ok).length}/{reply.delivery_log.length} recipients
        </p>
      )}
    </div>
  );
}

export default AdminCorrespondenceTab;
